import TemplateConfigurationModel from '../models/templating/api/TemplateConfigurationModel';
import TemplateFormFieldModel from '../models/templating/api/TemplateFormFieldModel';
import TemplateFormFieldOptionModel from '../models/templating/api/TemplateFormFieldOptionModel';
import TemplateFormModel from '../models/templating/api/TemplateFormModel';
import TemplateFormSectionModel from '../models/templating/api/TemplateFormSectionModel';
import { ImportedTemplateFormCsvField, ImportedTemplateFormCsvSection } from './templateFormCsv';

export interface StrictCsvSyncSectionPlan {
    title: string;
    priority: number;
}

export interface StrictCsvSyncFieldPlan {
    associatedVariable: string;
    sectionTitle: string;
    sectionPriority: number;
    field: ImportedTemplateFormCsvField;
}

export interface StrictCsvSyncPlan {
    sections: StrictCsvSyncSectionPlan[];
    fieldsByAssociatedVariable: Record<string, StrictCsvSyncFieldPlan>;
    newCoreFields: StrictCsvSyncFieldPlan[];
}

export interface StrictCsvSyncNewField {
    associatedVariable: string;
    title: string;
    sectionTitle: string;
    fieldType: string;
}

export interface StrictCsvSyncFieldChange {
    associatedVariable: string;
    fieldId: string;
    title: string;
    fromSectionTitle?: string;
    toSectionTitle: string;
    changedProps: string[];
}

export interface StrictCsvSyncResult {
    templateConfiguration: TemplateConfigurationModel;
    errors: string[];
    missingSections: StrictCsvSyncSectionPlan[];
    fieldChanges: StrictCsvSyncFieldChange[];
    newFields: StrictCsvSyncNewField[];
}

const normalizeAssociatedVariable = (value?: string | null): string => value?.trim() ?? '';

const getFieldDisplayName = (field: { title?: string; label?: string; associatedVariable?: string; id?: string }): string => {
    return field.title ?? field.label ?? field.associatedVariable ?? field.id ?? 'Untitled Field';
};

const cloneField = (field: TemplateFormFieldModel): TemplateFormFieldModel => ({
    ...field,
    options: [...(field.options ?? [])],
});

const cloneSection = (section: TemplateFormSectionModel): TemplateFormSectionModel => ({
    ...section,
    templateFormFields: (section.templateFormFields ?? []).map(cloneField),
});

const cloneTemplateForm = (templateForm?: TemplateFormModel): TemplateFormModel | undefined => {
    if (!templateForm) return templateForm;

    return {
        ...templateForm,
        templateFormSections: (templateForm.templateFormSections ?? []).map(cloneSection),
    };
};

const collectTemplateFieldsByAssociatedVariable = (
    template: TemplateConfigurationModel,
    templateLabel: string,
): { errors: string[]; fieldsByAssociatedVariable: Map<string, TemplateFormFieldModel> } => {
    const errors: string[] = [];
    const fieldsByAssociatedVariable = new Map<string, TemplateFormFieldModel>();

    (template.templateForm?.templateFormSections ?? []).forEach((section, sectionIndex) => {
        (section.templateFormFields ?? []).forEach(field => {
            const associatedVariable = normalizeAssociatedVariable(field.associatedVariable);
            const fieldName = getFieldDisplayName(field);

            if (!associatedVariable) {
                errors.push(`${templateLabel} field "${fieldName}" in section "${section.title ?? sectionIndex + 1}" is missing associatedVariable.`);
                return;
            }

            if (fieldsByAssociatedVariable.has(associatedVariable)) {
                errors.push(`${templateLabel} has duplicate associatedVariable "${associatedVariable}".`);
                return;
            }

            fieldsByAssociatedVariable.set(associatedVariable, field);
        });
    });

    return { errors, fieldsByAssociatedVariable };
};

const getPlanForTemplate = (
    template: TemplateConfigurationModel,
    plan: StrictCsvSyncPlan,
): { requiredSections: StrictCsvSyncSectionPlan[] } => {
    const requiredSectionTitles = new Set<string>();

    (template.templateForm?.templateFormSections ?? []).forEach(section => {
        (section.templateFormFields ?? []).forEach(field => {
            const associatedVariable = normalizeAssociatedVariable(field.associatedVariable);
            if (!associatedVariable) return;

            const fieldPlan = plan.fieldsByAssociatedVariable[associatedVariable];
            if (!fieldPlan) return;

            requiredSectionTitles.add(fieldPlan.sectionTitle);
        });
    });

    return {
        requiredSections: plan.sections.filter(section => requiredSectionTitles.has(section.title)),
    };
};

const mergeFieldOptions = (
    existingOptions: TemplateFormFieldOptionModel[] | undefined,
    importedOptions: TemplateFormFieldOptionModel[] | undefined,
    templateFormFieldId: string,
): TemplateFormFieldOptionModel[] => {
    const currentOptions = existingOptions ?? [];
    const nextOptions = importedOptions ?? [];

    const existingByValue = new Map<string, TemplateFormFieldOptionModel>();
    const existingByLabel = new Map<string, TemplateFormFieldOptionModel>();

    currentOptions.forEach(option => {
        if (option.value && !existingByValue.has(option.value)) {
            existingByValue.set(option.value, option);
        }
        if (option.label && !existingByLabel.has(option.label)) {
            existingByLabel.set(option.label, option);
        }
    });

    return nextOptions.map((option, index) => {
        const matchedExistingOption =
            (option.value ? existingByValue.get(option.value) : undefined) ??
            (option.label ? existingByLabel.get(option.label) : undefined) ??
            currentOptions[index];

        return {
            ...option,
            id: matchedExistingOption?.id,
            templateFormFieldId,
            priority: option.priority ?? index,
        };
    });
};

const getChangedFieldProps = (
    existingField: TemplateFormFieldModel,
    updatedField: TemplateFormFieldModel,
    fromSectionTitle: string | undefined,
    toSectionTitle: string,
): string[] => {
    const changedProps: string[] = [];
    const trackedProps: (keyof TemplateFormFieldModel)[] = [
        'title',
        'label',
        'fieldType',
        'placeholder',
        'defaultValue',
        'tip',
        'isRequired',
        'instructionsMarkdown',
        'associatedVariable',
        'priority',
        'suggestions',
        'fileFormats',
        'recentlyAdded',
    ];

    trackedProps.forEach(prop => {
        if (JSON.stringify(existingField[prop] ?? null) !== JSON.stringify(updatedField[prop] ?? null)) {
            changedProps.push(String(prop));
        }
    });

    if (JSON.stringify(existingField.conditionalDefaults ?? null) !== JSON.stringify(updatedField.conditionalDefaults ?? null)) {
        changedProps.push('conditionalDefaults');
    }

    if (JSON.stringify(existingField.options ?? []) !== JSON.stringify(updatedField.options ?? [])) {
        changedProps.push('options');
    }

    if ((fromSectionTitle ?? '') !== toSectionTitle) {
        changedProps.push('section');
    }

    return changedProps;
};

export const buildStrictCsvSyncPlan = (
    importedSections: ImportedTemplateFormCsvSection[],
    coreTemplate: TemplateConfigurationModel,
): { plan?: StrictCsvSyncPlan; errors: string[] } => {
    const errors: string[] = [];
    const fieldsByAssociatedVariable: Record<string, StrictCsvSyncFieldPlan> = {};
    const sections: StrictCsvSyncSectionPlan[] = [];
    const newCoreFields: StrictCsvSyncFieldPlan[] = [];
    const seenSectionTitles = new Set<string>();

    importedSections.forEach((section, sectionIndex) => {
        const title = section.title?.trim();
        if (!title) {
            errors.push(`CSV section ${sectionIndex + 1} is missing a title.`);
            return;
        }

        if (seenSectionTitles.has(title)) {
            errors.push(`CSV has duplicate section title "${title}".`);
            return;
        }

        seenSectionTitles.add(title);
        sections.push({
            title,
            priority: section.priority ?? sectionIndex,
        });

        (section.fields ?? []).forEach((field, fieldIndex) => {
            const associatedVariable = normalizeAssociatedVariable(field.associatedVariable);

            if (!associatedVariable) {
                errors.push(`CSV field "${field.title ?? fieldIndex + 1}" in section "${title}" is missing associatedVariable.`);
                return;
            }

            if (fieldsByAssociatedVariable[associatedVariable]) {
                errors.push(`CSV has duplicate associatedVariable "${associatedVariable}".`);
                return;
            }

            fieldsByAssociatedVariable[associatedVariable] = {
                associatedVariable,
                sectionTitle: title,
                sectionPriority: section.priority ?? sectionIndex,
                field,
            };
        });
    });

    const coreFieldCollection = collectTemplateFieldsByAssociatedVariable(coreTemplate, 'Core module');
    errors.push(...coreFieldCollection.errors);

    const coreAssociatedVariables = new Set(coreFieldCollection.fieldsByAssociatedVariable.keys());
    Object.keys(fieldsByAssociatedVariable).forEach(associatedVariable => {
        if (!coreAssociatedVariables.has(associatedVariable)) {
            newCoreFields.push(fieldsByAssociatedVariable[associatedVariable]);
        }
    });

    if (errors.length > 0) {
        return { errors };
    }

    return {
        plan: {
            sections,
            fieldsByAssociatedVariable,
            newCoreFields,
        },
        errors: [],
    };
};

export const buildStrictCsvSectionCreationPayload = (
    template: TemplateConfigurationModel,
    plan: StrictCsvSyncPlan,
    allowNewFields: boolean,
): StrictCsvSyncResult => {
    const fieldCollection = collectTemplateFieldsByAssociatedVariable(template, 'Template');
    const errors = [...fieldCollection.errors];

    const planForTemplate = getPlanForTemplate(template, plan);

    const existingSectionsByTitle = new Map<string, TemplateFormSectionModel>();
    (template.templateForm?.templateFormSections ?? []).forEach(section => {
        if (section.title) {
            existingSectionsByTitle.set(section.title, section);
        }
    });

    const requiredSectionTitles = new Set(planForTemplate.requiredSections.map(section => section.title));

    if (allowNewFields) {
        plan.newCoreFields.forEach(fieldPlan => requiredSectionTitles.add(fieldPlan.sectionTitle));
    }

    const missingSections = plan.sections.filter(section => requiredSectionTitles.has(section.title) && !existingSectionsByTitle.has(section.title));

    if (errors.length > 0) {
        return {
            templateConfiguration: template,
            errors,
            missingSections,
            fieldChanges: [],
            newFields: [],
        };
    }

    if (missingSections.length === 0) {
        return {
            templateConfiguration: template,
            errors: [],
            missingSections: [],
            fieldChanges: [],
            newFields: [],
        };
    }

    const existingSections = template.templateForm?.templateFormSections ?? [];
    const templateFormId = template.templateForm?.id;

    const templateConfiguration: TemplateConfigurationModel = {
        ...template,
        templateForm: cloneTemplateForm(template.templateForm),
    } as TemplateConfigurationModel;

    templateConfiguration.templateForm = {
        ...templateConfiguration.templateForm,
        templateFormSections: [
            ...existingSections.map(section => ({
                ...section,
                templateFormFields: (section.templateFormFields ?? []).map(cloneField),
            })),
            ...missingSections.map(section => ({
                title: section.title,
                priority: section.priority,
                templateFormId,
                templateFormFields: [],
            } as TemplateFormSectionModel)),
        ],
    };

    return {
        templateConfiguration,
        errors: [],
        missingSections,
        fieldChanges: [],
        newFields: [],
    };
};

export const buildStrictCsvFieldSyncPayload = (
    template: TemplateConfigurationModel,
    plan: StrictCsvSyncPlan,
    allowNewFields: boolean,
    allowSectionsWithoutIds: boolean = false,
): StrictCsvSyncResult => {
    const fieldCollection = collectTemplateFieldsByAssociatedVariable(template, 'Template');
    const errors = [...fieldCollection.errors];

    const currentAssociatedVariables = new Set(fieldCollection.fieldsByAssociatedVariable.keys());

    const existingSectionsByTitle = new Map<string, TemplateFormSectionModel>();
    (template.templateForm?.templateFormSections ?? []).forEach(section => {
        if (section.title) {
            existingSectionsByTitle.set(section.title, section);
        }
    });

    const planForTemplate = getPlanForTemplate(template, plan);

    const newFieldPlans = allowNewFields
        ? plan.newCoreFields.filter(fieldPlan => !currentAssociatedVariables.has(fieldPlan.associatedVariable))
        : [];

    const requiredSections = new Map<string, StrictCsvSyncSectionPlan>();
    planForTemplate.requiredSections.forEach(section => requiredSections.set(section.title, section));
    newFieldPlans.forEach(fieldPlan => {
        const sectionPlan = plan.sections.find(section => section.title === fieldPlan.sectionTitle);
        if (sectionPlan) {
            requiredSections.set(sectionPlan.title, sectionPlan);
        }
    });

    Array.from(requiredSections.values()).forEach(section => {
        if (!existingSectionsByTitle.has(section.title)) {
            errors.push(`Template is missing target section "${section.title}". Create sections first before moving fields.`);
        }
    });

    if (errors.length > 0) {
        return {
            templateConfiguration: template,
            errors,
            missingSections: Array.from(requiredSections.values()).filter(section => !existingSectionsByTitle.has(section.title)),
            fieldChanges: [],
            newFields: [],
        };
    }

    const updatedFieldsByTargetSection = new Map<string, TemplateFormFieldModel[]>();
    const fieldSectionById = new Map<string, string>();
    const fieldChanges: StrictCsvSyncFieldChange[] = [];

    (template.templateForm?.templateFormSections ?? []).forEach(section => {
        const sectionTitle = section.title ?? '';
        updatedFieldsByTargetSection.set(
            sectionTitle,
            (section.templateFormFields ?? []).map(cloneField),
        );

        (section.templateFormFields ?? []).forEach(field => {
            if (field.id) {
                fieldSectionById.set(field.id, sectionTitle);
            }
        });
    });

    (template.templateForm?.templateFormSections ?? []).forEach(currentSection => {
        (currentSection.templateFormFields ?? []).forEach(existingField => {
            const associatedVariable = normalizeAssociatedVariable(existingField.associatedVariable);
            const fieldPlan = plan.fieldsByAssociatedVariable[associatedVariable];
            if (!fieldPlan) {
                return;
            }

            const targetSection = existingSectionsByTitle.get(fieldPlan.sectionTitle);
            if (!targetSection) {
                errors.push(`Target section "${fieldPlan.sectionTitle}" does not exist.`);
                return;
            }

            if (!targetSection.id && !allowSectionsWithoutIds) {
                errors.push(`Target section "${fieldPlan.sectionTitle}" does not have a server id yet.`);
                return;
            }

            const updatedField: TemplateFormFieldModel = {
                ...existingField,
                title: fieldPlan.field.title,
                label: fieldPlan.field.label ?? existingField.label,
                fieldType: fieldPlan.field.fieldType || existingField.fieldType,
                associatedVariable: fieldPlan.associatedVariable,
                placeholder: fieldPlan.field.placeholder ?? existingField.placeholder ?? '',
                tip: fieldPlan.field.tip ?? existingField.tip ?? '',
                defaultValue: fieldPlan.field.defaultValue ?? existingField.defaultValue ?? '',
                conditionalDefaults: fieldPlan.field.conditionalDefaults ?? existingField.conditionalDefaults,
                isRequired: fieldPlan.field.isRequired,
                priority: fieldPlan.field.priority,
                templateFormSectionId: targetSection.id ?? existingField.templateFormSectionId,
                options: mergeFieldOptions(existingField.options, fieldPlan.field.options, existingField.id),
            };

            const changedProps = getChangedFieldProps(existingField, updatedField, currentSection.title, fieldPlan.sectionTitle);

            fieldChanges.push({
                associatedVariable,
                fieldId: existingField.id,
                title: updatedField.title ?? associatedVariable,
                fromSectionTitle: currentSection.title,
                toSectionTitle: fieldPlan.sectionTitle,
                changedProps,
            });

            const sourceSectionTitle = fieldSectionById.get(existingField.id) ?? currentSection.title ?? '';
            const sourceSectionFields = updatedFieldsByTargetSection.get(sourceSectionTitle) ?? [];
            updatedFieldsByTargetSection.set(
                sourceSectionTitle,
                sourceSectionFields.filter(field => field.id !== existingField.id),
            );

            if (!updatedFieldsByTargetSection.has(fieldPlan.sectionTitle)) {
                updatedFieldsByTargetSection.set(fieldPlan.sectionTitle, []);
            }

            updatedFieldsByTargetSection.set(
                fieldPlan.sectionTitle,
                [
                    ...(updatedFieldsByTargetSection.get(fieldPlan.sectionTitle) ?? []).filter(field => field.id !== existingField.id),
                    updatedField,
                ],
            );

            fieldSectionById.set(existingField.id, fieldPlan.sectionTitle);
        });
    });

    if (errors.length > 0) {
        return {
            templateConfiguration: template,
            errors,
            missingSections: [],
            fieldChanges: [],
            newFields: [],
        };
    }

    const updatedFieldIds = new Set<string>();
    updatedFieldsByTargetSection.forEach(fields => {
        fields.forEach(field => {
            if (field.id) {
                updatedFieldIds.add(field.id);
            }
        });
    });

    const existingFieldIds = new Set<string>();
    (template.templateForm?.templateFormSections ?? []).forEach(section => {
        (section.templateFormFields ?? []).forEach(field => existingFieldIds.add(field.id));
    });

    if (!allowNewFields && updatedFieldIds.size !== existingFieldIds.size) {
        errors.push('Field sync would change the number of fields in the template. New or removed fields are not allowed.');
    }

    existingFieldIds.forEach(fieldId => {
        if (!updatedFieldIds.has(fieldId)) {
            errors.push(`Field sync would remove field id ${fieldId}. New or removed fields are not allowed.`);
        }
    });

    if (errors.length > 0) {
        return {
            templateConfiguration: template,
            errors,
            missingSections: [],
            fieldChanges: [],
            newFields: [],
        };
    }

    const newFields: StrictCsvSyncNewField[] = [];

    newFieldPlans.forEach(fieldPlan => {
        const targetSection = existingSectionsByTitle.get(fieldPlan.sectionTitle);
        if (!targetSection?.title) {
            return;
        }

        const createdField = ({
            title: fieldPlan.field.title,
            label: fieldPlan.field.label,
            fieldType: fieldPlan.field.fieldType,
            associatedVariable: fieldPlan.associatedVariable,
            placeholder: fieldPlan.field.placeholder ?? '',
            tip: fieldPlan.field.tip ?? '',
            defaultValue: fieldPlan.field.defaultValue ?? '',
            conditionalDefaults: fieldPlan.field.conditionalDefaults,
            isRequired: fieldPlan.field.isRequired,
            priority: fieldPlan.field.priority,
            templateFormSectionId: targetSection.id,
            options: (fieldPlan.field.options ?? []).map(option => ({
                ...option,
                id: undefined,
                templateFormFieldId: undefined,
            })),
        } as unknown) as TemplateFormFieldModel;

        if (!updatedFieldsByTargetSection.has(fieldPlan.sectionTitle)) {
            updatedFieldsByTargetSection.set(fieldPlan.sectionTitle, []);
        }

        updatedFieldsByTargetSection.set(
            fieldPlan.sectionTitle,
            [
                ...(updatedFieldsByTargetSection.get(fieldPlan.sectionTitle) ?? []),
                createdField,
            ],
        );
        newFields.push({
            associatedVariable: fieldPlan.associatedVariable,
            title: fieldPlan.field.title ?? fieldPlan.associatedVariable,
            sectionTitle: fieldPlan.sectionTitle,
            fieldType: fieldPlan.field.fieldType,
        });
    });

    const updatedSections: TemplateFormSectionModel[] = [];
    const templateFormId = template.templateForm?.id;
    const sectionPlansByTitle = new Map<string, StrictCsvSyncSectionPlan>();
    plan.sections.forEach(sectionPlan => sectionPlansByTitle.set(sectionPlan.title, sectionPlan));

    (template.templateForm?.templateFormSections ?? []).forEach(section => {
        const sectionPlan = section.title ? sectionPlansByTitle.get(section.title) : undefined;
        const targetFields = section.title ? (updatedFieldsByTargetSection.get(section.title) ?? []) : [];

        updatedSections.push({
            ...section,
            title: sectionPlan?.title ?? section.title,
            priority: sectionPlan?.priority ?? section.priority,
            templateFormId: section.templateFormId ?? templateFormId,
            templateFormFields: [...targetFields].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
        });
    });

    return {
        templateConfiguration: {
            ...template,
            templateForm: {
                ...cloneTemplateForm(template.templateForm),
                templateFormSections: updatedSections,
            },
        },
        errors: [],
        missingSections: [],
        fieldChanges,
        newFields,
    };
};
